/**
 * Opening WhatsApp from a stored phone number.
 *
 * No API integration: wa.me takes a number in international format and opens
 * the conversation in whatever client the marketer has. That is the whole MVP.
 */

/** Default country when a number was saved without a dialling code. */
const DEFAULT_COUNTRY_CODE =
  process.env.REACT_APP_DEFAULT_COUNTRY_CODE || '234';

/**
 * Normalises a stored number to the digits-only international form wa.me needs.
 *
 * Handles the three shapes numbers actually get typed in as: +234 803…,
 * 0803… (national, leading zero), and 803… (bare).
 */
export const normalisePhone = (
  phone: string,
  countryCode = DEFAULT_COUNTRY_CODE
): string | null => {
  if (!phone) return null;

  const trimmed = phone.trim();
  const hadPlus = trimmed.startsWith('+');
  let digits = trimmed.replace(/\D/g, '');

  if (!digits) return null;

  if (hadPlus || digits.startsWith('00')) {
    digits = digits.replace(/^00/, '');
  } else if (digits.startsWith('0')) {
    // National format: drop the trunk zero and prepend the country code.
    digits = `${countryCode}${digits.slice(1)}`;
  } else if (!digits.startsWith(countryCode)) {
    digits = `${countryCode}${digits}`;
  }

  // Shorter than this is a typo, not a phone number.
  return digits.length >= 10 ? digits : null;
};

export const whatsappUrl = (phone: string, message?: string): string | null => {
  const number = normalisePhone(phone);
  if (!number) return null;

  const query = message ? `?text=${encodeURIComponent(message)}` : '';
  return `https://wa.me/${number}${query}`;
};

/** The opener the playbook uses, with the prospect's own details filled in. */
export const defaultOutreachMessage = (options: {
  name: string;
  companyName?: string;
  senderName?: string;
}): string => {
  const firstName = options.name.trim().split(/\s+/)[0] || 'there';
  const work = options.companyName
    ? `your work at ${options.companyName}`
    : 'your work with hospitality businesses';

  return `Hello ${firstName}, I came across ${work} and wanted to discuss a potential technology partnership with Nexasolutra.${
    options.senderName ? `\n\n— ${options.senderName}` : ''
  }`;
};
